import React, { useEffect, useState } from 'react';
import { ChevronDown, Store, MapPin, Building2, Check } from 'lucide-react';
import { useBranchStore } from '@/stores/branch.store';
import { useAuthStore } from '@/stores/auth.store';
import { branchService } from '@/services/branch.service';
import toast from 'react-hot-toast';

export const BranchSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { tenant } = useAuthStore();
  const { currentBranch, branches, mainVendor, setCurrentBranch, setBranches, setMainVendor } = useBranchStore();

  useEffect(() => {
    if (tenant?.id) {
      loadBranches();
    }
  }, [tenant?.id]);

  const loadBranches = async () => {
    if (!tenant?.id) return;

    setIsLoading(true);
    try {
      const hierarchy = await branchService.getBranchHierarchy(tenant.id);
      setMainVendor(hierarchy.mainVendor);
      setBranches(hierarchy.branches);

      // Set current branch to main vendor if not set
      if (!currentBranch) {
        setCurrentBranch(hierarchy.mainVendor);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBranchSwitch = (branchId: string) => {
    const branch = branchId === mainVendor?.id
      ? mainVendor
      : branches.find(b => b.id === branchId);

    if (branch) {
      setCurrentBranch(branch);
      setIsOpen(false);
      toast.success(`Switched to ${branch.name}`);
    }
  };

  const allLocations = [
    ...(mainVendor ? [mainVendor] : []),
    ...branches
  ];

  const activeLocations = allLocations.filter(loc => loc.isActive);

  if (isLoading || !currentBranch) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg animate-pulse">
        <div className="w-5 h-5 bg-gray-300 rounded"></div>
        <div className="w-32 h-4 bg-gray-300 rounded"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 min-w-[200px]"
      >
        <div className="flex items-center flex-1 space-x-2">
          {currentBranch.isMainBranch ? (
            <Building2 className="w-5 h-5 text-primary-600" />
          ) : (
            <Store className="w-5 h-5 text-primary-600" />
          )}
          <div className="flex-1 text-left">
            <div className="text-sm font-medium text-gray-900 truncate">
              {currentBranch.name}
            </div>
            <div className="text-xs text-gray-500">
              {currentBranch.isMainBranch ? 'Main Location' : currentBranch.branchCode}
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Select Location</h3>
              <p className="text-xs text-gray-500 mt-1">
                {activeLocations.length} location{activeLocations.length !== 1 ? 's' : ''} available
              </p>
            </div>

            {/* All Locations Option */}
            <div className="p-2">
              <button
                onClick={() => {
                  setCurrentBranch(null);
                  setIsOpen(false);
                  toast.success('Viewing all locations');
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 ${
                  !currentBranch ? 'bg-primary-50 border border-primary-200' : ''
                }`}
              >
                <MapPin className="w-5 h-5 text-primary-600" />
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-gray-900">All Locations</div>
                  <div className="text-xs text-gray-500">View combined data</div>
                </div>
                {!currentBranch && (
                  <Check className="w-5 h-5 text-primary-600" />
                )}
              </button>
            </div>

            {/* Main Vendor */}
            {mainVendor && (
              <div className="p-2 border-t border-gray-100">
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Main Location
                </div>
                <button
                  onClick={() => handleBranchSwitch(mainVendor.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 ${
                    currentBranch?.id === mainVendor.id ? 'bg-primary-50 border border-primary-200' : ''
                  }`}
                >
                  <Building2 className="w-5 h-5 text-primary-600" />
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-gray-900">{mainVendor.name}</div>
                    <div className="text-xs text-gray-500 flex items-center mt-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {mainVendor.businessAddress}
                    </div>
                  </div>
                  {currentBranch?.id === mainVendor.id && (
                    <Check className="w-5 h-5 text-primary-600" />
                  )}
                </button>
              </div>
            )}

            {/* Branches */}
            {branches.length > 0 && (
              <div className="p-2 border-t border-gray-100">
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Branches ({branches.length})
                </div>
                <div className="space-y-1">
                  {branches.map((branch) => (
                    <button
                      key={branch.id}
                      onClick={() => handleBranchSwitch(branch.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 ${
                        currentBranch?.id === branch.id ? 'bg-primary-50 border border-primary-200' : ''
                      } ${!branch.isActive ? 'opacity-50' : ''}`}
                      disabled={!branch.isActive}
                    >
                      <Store className="w-5 h-5 text-primary-600" />
                      <div className="flex-1 text-left">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{branch.name}</span>
                          {!branch.isActive && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center mt-1">
                          <span className="font-mono mr-2">{branch.branchCode}</span>
                          <MapPin className="w-3 h-3 mr-1" />
                          {branch.businessAddress}
                        </div>
                      </div>
                      {currentBranch?.id === branch.id && (
                        <Check className="w-5 h-5 text-primary-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {branches.length === 0 && (
              <div className="p-6 text-center">
                <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No branches yet</p>
                <p className="text-xs text-gray-400 mt-1">Create your first branch to expand</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
